import React from "react";
import { Users2, X, Plus } from "lucide-react";
import {
  Section,
  SectionHeader,
  SectionTitleGroup,
  SectionTitle,
  SectionBody,
  AudienceChipRow,
  AudienceChip,
  ChipLabel,
  ChipRemoveBtn,
  AddAudienceChip,
  AddAudienceLabel,
} from "../../organisms/StoryBuilder/StoryBuilder.styles";
import { AudienceSkeleton } from "../../organisms/StoryBuilder/StoryBuilder.skeleton";
import {
  ChipBox,
  InlineAudienceInput,
  ModalField,
  ModalFieldLabel,
} from "./StoryAudienceSection.styles";
import type { StoryAudienceSectionProps } from "./StoryAudienceSection.types";

/** Figma draws the add-chip's plus at 16 in the modal frame, 14 in the builder. */
const PLUS_SIZE = { builder: 14, modal: 16 } as const;

export const StoryAudienceSection: React.FC<StoryAudienceSectionProps> = ({
  audiences,
  isPending = false,
  audienceInput,
  isAddingAudience,
  onAudienceInputChange,
  onStartAddingAudience,
  onAudienceKeyDown,
  onRemoveAudience,
  variant = "builder",
}) => {
  const chipRow = (
    <AudienceChipRow>
      {audiences.map((audience) => (
        <AudienceChip key={audience}>
          <ChipLabel>{audience}</ChipLabel>
          <ChipRemoveBtn
            type="button"
            aria-label={`Remove ${audience}`}
            onClick={() => onRemoveAudience?.(audience)}
          >
            <X size={12} />
          </ChipRemoveBtn>
        </AudienceChip>
      ))}

      {isAddingAudience ? (
        <InlineAudienceInput
          value={audienceInput}
          onChange={(e) => onAudienceInputChange(e.target.value)}
          onKeyDown={onAudienceKeyDown}
          placeholder="Type name and press Enter…"
          aria-label="New audience name"
          autoFocus
        />
      ) : (
        <AddAudienceChip
          type="button"
          aria-label="Add audience"
          onClick={onStartAddingAudience}
        >
          <Plus size={PLUS_SIZE[variant]} />
          <AddAudienceLabel>{variant === "modal" ? "Add audience" : "Add"}</AddAudienceLabel>
        </AddAudienceChip>
      )}
    </AudienceChipRow>
  );

  const body = isPending ? <AudienceSkeleton /> : chipRow;

  if (variant === "modal") {
    return (
      <ModalField as="div" data-testid="section-audience">
        {/* Not a labelable control — the box holds buttons, not an input. */}
        <ModalFieldLabel as="span">Audience</ModalFieldLabel>
        <ChipBox data-testid="audience-chip-box">{body}</ChipBox>
      </ModalField>
    );
  }

  return (
    <Section data-testid="section-audience">
      <SectionHeader>
        <SectionTitleGroup>
          <Users2 size={16} />
          <SectionTitle>Audiences</SectionTitle>
        </SectionTitleGroup>
      </SectionHeader>
      <SectionBody>{body}</SectionBody>
    </Section>
  );
};

export default StoryAudienceSection;
